import { WebCryptoAES } from "@cirrus/crypto/WebCryptoAES"
import { getConfig } from "@cirrus/config"
import sessionKeyActions from "@cirrus-delivery/v1/pages/SessionKey/actions.jsx"
import SessionKey from "@cirrus-delivery/v1/pages/SessionKey/sessionKey"
import {
  checkIfSafeExamBrowser,
  obtainHeaders,
  withSafeExamBrowser,
} from "@cirrus/legacy/app-common/utils/secureExam.js"
import axios from "axios"
import { getEventBus } from "@cirrus/core/events/provider"

withSafeExamBrowser()

export const httpRequest = axios.create({
  headers: {
    Authorization: "Bearer " + sessionStorage.getItem(getConfig().TokenKey),
    "Content-Type": "application/json;charset=UTF-8",
  },
})

async function prepareData(response) {
  if (response.data && response.headers["content-type"] === "encrypted/json; charset=utf-8") {
    const sessionKey = SessionKey.get()

    if (!sessionKey) {
      sessionKeyActions.decryptionFailed()
      throw new axios.Cancel("Invalid session key.")
    }

    const decryptedData = await (async () => {
      try {
        const decryptedJSON = await new WebCryptoAES().decrypt(response.data, sessionKey)
        return JSON.parse(decryptedJSON)
      } catch {
        return null
      }
    })()

    if (!decryptedData) {
      sessionKeyActions.decryptionFailed()
      throw new axios.Cancel("Error decrypting data.")
    }

    return decryptedData
  }

  return response.data
}

httpRequest.interceptors.response.use(
  async (response) => {
    response.data = await prepareData(response)
    return response
  },
  async (error) => {
    if (error.response) {
      if (error.response.status == 401) {
        document.location.replace("/")
      }

      if (error.response.status == 403) {
         if (notExcludedApi(error.config.url)) getEventBus().emit("Auth > Forbidden", error)
      }

      if (error.response.status == 424) {
        sessionKeyActions.decryptionFailed()
        throw new axios.Cancel("Operation canceled by the user. Invalid session key.")
      }

      error.response.data = await prepareData(error.response)
    } else {
      error.isNetworkError = true
    }

    return Promise.reject(error)
  },
)

httpRequest.interceptors.request.use((defaults) => {
  const config = { ...defaults }

  config.headers["x-cirrus"] = "Cirrus Assessment"
  config.headers["x-cirrus-http-client"] = "axios / httpRequest"

  if (checkIfSafeExamBrowser()) {
    const sebHeaders = obtainHeaders()

    config.headers = {
      ...config.headers,
      ...sebHeaders,
    }
  }

  return config
})

export const { CancelToken } = axios
export const { isCancel } = axios

export const cancellable = (requestToWrap) => {
  let source = null

  const cancel = () => {
    if (source != null) {
      source.cancel()
      source = null
    }
  }

  const request = (...args) => {
    const localSource = CancelToken.source()

    source = localSource

    return requestToWrap(localSource.token, ...args).then(
      (response) => {
        if (source === localSource) {
          source = null
        }

        return response
      },

      (error) => {
        if (source === localSource) {
          source = null
        }

        throw error
      },
    )
  }

  return { cancel, request }
}

const notExcludedApi = (url = "") =>
  // TODO rude fix, need support for such apis
  !url.includes("marking-scheme")
  && !url.includes("author/marking")
